// src/pages/maintenance/CompleteWorkOrderModal.js
// Closes out a work order: parts used, labor, root cause/corrective action, and — for a
// calibration work order — the signer name needed to generate the locked certificate (SQF 11.6).
// See docs/MAINTENANCE_MODULE.md.
import React, { useState, useEffect, useMemo } from 'react';
import maintenanceService from '../../services/maintenanceService';
import SearchableSelect from '../../components/SearchableSelect';
import VendorPicker from './VendorPicker';

const FAILURE_TYPE_OPTIONS = ['mechanical', 'electrical', 'wear', 'operator_error', 'contamination', 'unknown'];
const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function CompleteWorkOrderModal({ workOrder, onSave, onClose }) {
  // Pre-planned parts (from a PmTemplate) carry a workOrderPartId; parts added here at close-out
  // carry a partId instead — backend/services/maintenanceWoService.js's closeWorkOrder() handles
  // both the same way (cost snapshot + stock deduction), creating the WorkOrderPart row for the
  // ad-hoc ones on the fly.
  const [partsUsed, setPartsUsed] = useState(
    (workOrder.Parts || []).map(p => ({
      workOrderPartId: p.id, partName: p.Part?.name, unit: p.Part?.unitOfMeasure,
      unitCost: p.unitCostSnapshot != null ? Number(p.unitCostSnapshot) : Number(p.Part?.unitCost || 0),
      quantityUsed: p.quantityPlanned || 0,
    }))
  );
  const [allParts, setAllParts] = useState([]);
  const [addPartId, setAddPartId] = useState('');
  const [actualHours, setActualHours] = useState(workOrder.estimatedHours || '');
  const [laborCost, setLaborCost] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [failureType, setFailureType] = useState('');
  const [vendorId, setVendorId] = useState(workOrder.vendorId ? String(workOrder.vendorId) : '');
  const [vendorTechnicianName, setVendorTechnicianName] = useState(workOrder.vendorTechnicianName || '');
  const [signedByName, setSignedByName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { maintenanceService.getParts().then(setAllParts).catch(() => setAllParts([])); }, []);

  const updatePartQty = (idx, qty) => {
    setPartsUsed(list => list.map((p, i) => i === idx ? { ...p, quantityUsed: qty } : p));
  };

  const removePart = (idx) => {
    setPartsUsed(list => list.filter((_, i) => i !== idx));
  };

  const addPart = () => {
    if (!addPartId) return;
    const part = allParts.find(p => p.id === addPartId);
    if (!part) return;
    if (partsUsed.some(p => !p.workOrderPartId && p.partId === part.id)) { setAddPartId(''); return; } // already added
    setPartsUsed(list => [...list, {
      partId: part.id, partName: part.name, unit: part.unitOfMeasure,
      unitCost: Number(part.unitCost || 0), quantityUsed: 1,
    }]);
    setAddPartId('');
  };

  const estimatedPartsCost = useMemo(
    () => partsUsed.reduce((sum, p) => sum + (parseFloat(p.quantityUsed) || 0) * (p.unitCost || 0), 0),
    [partsUsed]
  );
  const estimatedTotalCost = estimatedPartsCost + (parseFloat(laborCost) || 0);

  const partOptions = allParts
    .filter(p => !partsUsed.some(u => !u.workOrderPartId && u.partId === p.id))
    .map(p => ({ value: p.id, label: `${p.name}${p.unitCost != null ? ` — ${fmtMoney(p.unitCost)}/${p.unitOfMeasure || 'unit'}` : ''}` }));

  const submit = async (e) => {
    e.preventDefault();
    if (workOrder.isCalibrationWo && !signedByName.trim()) {
      setError('Signer name is required for a calibration work order.');
      return;
    }
    setSaving(true); setError('');
    try {
      const result = await maintenanceService.completeWorkOrder(workOrder.id, {
        partsUsed: partsUsed.map(p => ({
          workOrderPartId: p.workOrderPartId, partId: p.partId,
          quantityUsed: parseFloat(p.quantityUsed) || 0,
        })),
        actualHours: actualHours !== '' ? parseFloat(actualHours) : null,
        laborCost: laborCost !== '' ? parseFloat(laborCost) : null,
        rootCause, correctionNotes,
        failureType: failureType || null,
        vendorId: vendorId || null,
        vendorTechnicianName: vendorTechnicianName.trim() || null,
        signedByName: workOrder.isCalibrationWo ? signedByName : undefined,
      });
      onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to complete work order.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Complete Work Order — {workOrder.woNumber}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

              <h6>Parts Used <span className="text-muted fw-normal" style={{ fontSize: 12 }}>(optional)</span></h6>
              {partsUsed.length > 0 && (
                <table className="table table-sm mb-2">
                  <thead><tr><th>Part</th><th style={{ width: 120 }}>Qty Used</th><th style={{ width: 100 }}>Unit Cost</th><th style={{ width: 100 }}>Cost</th><th style={{ width: 1 }}></th></tr></thead>
                  <tbody>
                    {partsUsed.map((p, idx) => (
                      <tr key={p.workOrderPartId || `new-${p.partId}`}>
                        <td style={{ fontSize: 13 }}>
                          {p.partName}
                          {!p.workOrderPartId && <span className="badge bg-info-subtle text-info ms-2" style={{ fontSize: 10 }}>added now</span>}
                        </td>
                        <td>
                          <input className="form-control form-control-sm" value={p.quantityUsed} onChange={e => updatePartQty(idx, e.target.value)} />
                        </td>
                        <td style={{ fontSize: 13 }}>{fmtMoney(p.unitCost)}</td>
                        <td style={{ fontSize: 13 }}>{fmtMoney((parseFloat(p.quantityUsed) || 0) * (p.unitCost || 0))}</td>
                        <td>
                          <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removePart(idx)}>&times;</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="d-flex gap-2 align-items-start mb-3" style={{ maxWidth: 480 }}>
                <div className="flex-grow-1">
                  <SearchableSelect
                    options={partOptions}
                    value={addPartId}
                    onChange={val => setAddPartId(val || '')}
                    placeholder="Add a part used…"
                    size="sm"
                  />
                </div>
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={!addPartId} onClick={addPart}>Add</button>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Actual Hours</label>
                  <input className="form-control form-control-sm" value={actualHours} onChange={e => setActualHours(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Labor Cost ($)</label>
                  <input className="form-control form-control-sm" value={laborCost} onChange={e => setLaborCost(e.target.value)} />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Parts {fmtMoney(estimatedPartsCost)} + Labor {fmtMoney(laborCost || 0)} = <strong>{fmtMoney(estimatedTotalCost)}</strong> total
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Failure Type</label>
                  <select className="form-select form-select-sm" value={failureType} onChange={e => setFailureType(e.target.value)}>
                    <option value="">—</option>
                    {FAILURE_TYPE_OPTIONS.map(f => <option key={f} value={f}>{labelize(f)}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Vendor (if outside service)</label>
                  <VendorPicker value={vendorId} onChange={val => setVendorId(val || '')} />
                </div>
                {vendorId && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Technician Name</label>
                    <input className="form-control form-control-sm" value={vendorTechnicianName} onChange={e => setVendorTechnicianName(e.target.value)} />
                  </div>
                )}
                {workOrder.isCalibrationWo && (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Signed By <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={signedByName} onChange={e => setSignedByName(e.target.value)} placeholder="Full name for the certificate" />
                  </div>
                )}
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Root Cause</label>
                  <textarea className="form-control form-control-sm" rows={2} value={rootCause} onChange={e => setRootCause(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Corrective Action / Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={correctionNotes} onChange={e => setCorrectionNotes(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-success btn-sm" disabled={saving}>{saving ? 'Completing…' : 'Complete Work Order'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
